import { useId } from 'react'
import { PRACTICE_WEEKDAYS } from '@region13/shared-types'
import { cycleDay, dayStateLabel, DAY_LABEL, type DayState } from '../lib/practicePrefsCopy'

/** Three visual states matching the three logical ones. `focus-visible:ring`
 *  is not decoration: this is a three-state control, so a keyboard user has to
 *  be able to see which chip they are about to cycle. Never add `sr-only`
 *  here (it clips the outline). */
const chipClass = (state: 'ranked' | 'blocked' | 'unset', disabled: boolean) =>
  'flex min-h-[44px] min-w-[3.5rem] items-center justify-center rounded-lg border px-3 text-sm font-semibold transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-maroon/40 ' +
  (disabled
    ? 'cursor-not-allowed border-line bg-surface text-ink-soft opacity-40'
    : state === 'ranked'
      ? 'cursor-pointer border-brand-red-dark bg-brand-red-dark text-white'
      : state === 'blocked'
        ? 'cursor-pointer border-line bg-surface text-ink-soft line-through'
        : 'cursor-pointer border-line bg-surface text-ink hover:border-brand-red-dark/60')

/** Ranked-and-blocked weekday picker (spec D5). Click a day to rank it, click
 *  again to say it does not work at all, click again to clear. A day no
 *  reachable venue can cover is disabled: it is already impossible, so
 *  blocking it would be noise.
 *
 *  More precisely: ONLY an unset day is disabled. A day the coach has already
 *  ranked or blocked stays clickable even when it is unreachable, and that is a
 *  trap door rather than a nicety. Reachability narrows as venues are ranked,
 *  so a coach can block Wednesday - or rank Monday - and then rank a venue with
 *  no Wednesday or Monday permit, at which point the chip they just set would
 *  freeze with no way to cycle it back to clear. The usefulness of the rule is
 *  stopping a coach marking an already-impossible day; it was never to stop
 *  them undoing one.
 *
 *  The button group carries `role="group"` + `aria-labelledby` pointing at
 *  `label`, so two instances on one page (Upper Division's first/second
 *  practice) announce distinctly to a screen reader even though every
 *  button's own accessible name is just the day + state (`dayStateLabel`) -
 *  fixed here, once, rather than at each call site, so every caller gets it
 *  for free. This does NOT change any button's accessible name, so the
 *  single-picker case (Task 5's tests, e.g. exactly `'Monday, ranked 1'`) is
 *  unaffected. */
export function DayPreferencePicker({
  label,
  value,
  onChange,
  reachable,
  help,
}: {
  label: string
  value: DayState
  onChange: (v: DayState) => void
  reachable: Set<number>
  help?: string
}) {
  const labelId = useId()
  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-xs font-semibold text-ink-soft">{label}</span>
      <div role="group" aria-labelledby={labelId} className="flex flex-wrap gap-2">
        {PRACTICE_WEEKDAYS.map((day) => {
          const rank = value.ranked.indexOf(day)
          const state = rank !== -1 ? 'ranked' : value.blocked.includes(day) ? 'blocked' : 'unset'
          // Only an UNSET chip is ever disabled. A chip the coach has already
          // ranked or blocked stays clickable however unreachable the day
          // becomes, or they cannot undo what they set.
          const disabled = state === 'unset' && !reachable.has(day)
          return (
            <button
              key={day}
              type="button"
              onClick={() => onChange(cycleDay(value, day))}
              aria-pressed={state === 'ranked'}
              aria-label={dayStateLabel(day, value)}
              disabled={disabled}
              className={chipClass(state, disabled)}
            >
              {state === 'ranked' ? `${rank + 1}. ` : ''}
              {DAY_LABEL[day]}
            </button>
          )
        })}
      </div>
      <p className="text-xs text-ink-soft">
        {help ?? 'Click a day to rank it, click again if that day does not work at all, click a third time to clear it.'}
      </p>
      {value.ranked.length > 1 && (
        <ol className="flex flex-col gap-1">
          {value.ranked.map((day, i) => (
            <li key={day} className="text-sm text-ink-soft">
              {i + 1}. {DAY_LABEL[day]}
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
